import React, { useEffect, useRef, useState } from "react";
import { MessageSquareText, Send } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const initials = (n = "") => n.split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
const ago = (iso) => {
  const s = (Date.now() - new Date(iso)) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function AppNotesPanel({ appId, currentUserId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const listRef = useRef();

  const load = async () => { const r = await api.get(`/applications/${appId}/notes`); setNotes(r.data); };
  useEffect(() => { load(); }, [appId]); // eslint-disable-line
  useEffect(() => { if (listRef.current) { listRef.current.scrollTop = listRef.current.scrollHeight; } }, [notes.length]);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await api.post(`/applications/${appId}/notes`, { text }); setText(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); }
    finally { setSaving(false); }
  };

  return (
    <aside data-testid="app-notes-panel" className="bg-white rounded-xl border border-slate-200 flex flex-col lg:sticky lg:top-20 lg:h-[calc(100vh-6rem)] max-h-[70vh] lg:max-h-none">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <MessageSquareText className="w-4 h-4 text-orange-500" />
        <span className="heading-font text-sm font-semibold text-slate-900">Notes</span>
        <span data-testid="notes-count" className="ml-auto text-xs text-slate-500">{notes.length}</span>
      </div>
      <div className="p-3 border-b border-slate-100 space-y-2">
        <Textarea data-testid="note-input" value={text} onChange={e => setText(e.target.value)} placeholder="Write a note…"
          onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) add(); }}
          className="min-h-[72px] text-sm resize-none" />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-slate-400">Ctrl + Enter to post</span>
          <Button data-testid="note-submit" size="sm" onClick={add} disabled={saving || !text.trim()} className="h-7 bg-orange-500 hover:bg-orange-600 text-white">
            <Send className="w-3.5 h-3.5 mr-1" /> {saving ? "Adding…" : "Add note"}
          </Button>
        </div>
      </div>
      <div ref={listRef} data-testid="notes-list" className="flex-1 overflow-y-auto p-3 space-y-3">
        {notes.map(n => <NoteBubble key={n.id} n={n} mine={n.author_id === currentUserId} />)}
        {notes.length === 0 && <div className="text-xs text-slate-400 text-center py-8">No notes yet. Start the conversation above.</div>}
      </div>
    </aside>
  );
}

function NoteBubble({ n, mine }) {
  return (
    <div data-testid={`note-${n.id}`} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
      <div className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${mine ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-700"}`}>
        {initials(n.author_name)}
      </div>
      <div className={`max-w-[85%] ${mine ? "items-end" : ""} flex flex-col`}>
        <div className={`text-[11px] text-slate-500 mb-0.5 ${mine ? "text-right" : ""}`}>
          <b className="text-slate-700">{n.author_name}</b>{n.author_role && ` · ${n.author_role.replace(/_/g, " ")}`} · <span title={new Date(n.created_at).toLocaleString()}>{ago(n.created_at)}</span>
        </div>
        <div className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${mine ? "bg-orange-50 border border-orange-200 text-slate-900 rounded-tr-sm" : "bg-slate-50 border border-slate-200 text-slate-800 rounded-tl-sm"}`}>
          {n.text}
        </div>
      </div>
    </div>
  );
}
